import { useState } from 'react'

import { useIsHydrating } from '@muse-web/router'

import { type TAudioSimple, isTranscribableCategory } from '@core/models'
import { isAudioOwner } from '@features/audio'
import { isAnonymousAuthor } from '@features/author'
import { useCurrentUser } from '@features/session'

import { useAudioScreenTranscription } from '../store/useAudioScreenTranscription'

import { AsyncAudioTranscriptionContentContainer } from './AsyncAudioTranscriptionContentContainer'

type TAudioTranscriptionSectionContainerProps = {
    audio: TAudioSimple
}

export const AudioTranscriptionSectionContainer = ({
    audio,
}: TAudioTranscriptionSectionContainerProps) => {
    const { username: myUsername } = useCurrentUser()
    // `audio.transcribed` is immutable loader data and the backend only flips it once transcription
    // finishes, so the owner's own create/delete is tracked here. Re-requesting the loader instead
    // would remount the whole screen (dropping the open tab, comment drafts) and could still answer
    // `false` right after a create, letting the owner queue the job twice.
    const [hasTranscript, setHasTranscript] = useState(audio.transcribed)

    const isOwner = isAudioOwner(audio, myUsername)
    const [isOwnerAtLoad] = useState(isOwner)
    const initialTranscription = useAudioScreenTranscription()
    const isHydrating = useIsHydrating()

    // The owner also sees the section without a transcript — that's the create flow.
    const isVisible =
        !isAnonymousAuthor(audio.authorUsername) &&
        isTranscribableCategory(audio.categoryId) &&
        (hasTranscript || isOwner)

    // The create flow needs the viewer's plan, which the template has no param for, so an owner
    // without a transcript gets it on the pass after hydration.
    if (!isVisible || (!hasTranscript && isHydrating)) {
        return null
    }

    const handleCreated = () => {
        setHasTranscript(true)
    }

    const handleDeleted = () => {
        setHasTranscript(false)
    }

    return (
        <AsyncAudioTranscriptionContentContainer
            audioId={audio.id}
            hasTranscript={hasTranscript}
            isOwner={isOwner}
            initialIsOwner={isOwnerAtLoad}
            initialTranscription={initialTranscription}
            onCreated={handleCreated}
            onDeleted={handleDeleted}
        />
    )
}
